import React, { useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import nutraLogof from "../../assets/Images/home/favicon.png";

import 'swiper/css';
import 'swiper/css/pagination';

const FIRESTORE_BASE =
  'https://firestore.googleapis.com/v1/projects/web--reviews/databases/(default)/documents';

const FALLBACK_REVIEWS = [
  {
    id: 'f1',
    author: 'VIRAL ASODARIYA',
    date: 'JUNE 22, 2024',
    text: 'I have visited this company. company have well established manufacturing units which are fully well developed and properly maintained with hygienic conditions.',
    rating: 5,
    avatar: 'https://i.pravatar.cc/80?img=12',
  },
  {
    id: 'f2',
    author: 'DEV PANCHAL',
    date: 'JUNE 22, 2024',
    text: 'I have visited nutralike Health care, it is very good industry for making nutraceutical product, they are making wide range of nutraceutical products.',
    rating: 5,
    avatar: 'https://i.pravatar.cc/80?img=15',
  },
  {
    id: 'f3',
    author: 'KUSHAL SAHA',
    date: 'MARCH 14, 2024',
    text: 'Very nice experience in terms of behaviour, coordination and product quality also. Looking for long term association (Eccentra Healthcare)',
    rating: 5,
    avatar: 'https://i.pravatar.cc/80?img=20',
  },
];

// Firestore REST wraps every value in a type tag — unwrap to plain JS.
function unwrapFirestoreFields(fields = {}) {
  const out = {};
  for (const [key, val] of Object.entries(fields)) {
    if (val == null) continue;
    if ('stringValue' in val) out[key] = val.stringValue;
    else if ('integerValue' in val) out[key] = Number(val.integerValue);
    else if ('doubleValue' in val) out[key] = Number(val.doubleValue);
    else if ('booleanValue' in val) out[key] = val.booleanValue;
    else if ('timestampValue' in val) out[key] = val.timestampValue;
    else if ('arrayValue' in val)
      out[key] = (val.arrayValue.values || []).map((v) =>
        v.mapValue ? unwrapFirestoreFields(v.mapValue.fields) : v
      );
    else if ('mapValue' in val) out[key] = unwrapFirestoreFields(val.mapValue.fields);
  }
  return out;
}

function formatReviewDate(unixSeconds) {
  if (!unixSeconds) return '';
  const d = new Date(unixSeconds * 1000);
  return d
    .toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    .toUpperCase();
}

export default function Testemonial({
  companyName = 'Nutralike Formulation PVT.LTD',
  rating: ratingProp = 4.9,
  reviewCount: reviewCountProp = 122,
  reviewUrl = 'https://g.page/r/CeMXQGayJbHUEAE/review',
  widgetInstanceId = 'qtPjP1Par38Ql7V5l7TV',
  autoplayInterval = 5000,
}) {
  const swiperRef = useRef(null);
  const brandGreen = '#335235';
  const borderGreen = '#89A388';
  const cardBg = '#FFFFFF';

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [liveRating, setLiveRating] = useState(null);
  const [liveCount, setLiveCount] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const MAX_DOTS = 4;
  const dotCount = Math.min(MAX_DOTS, Math.max(1, reviews.length));
  const activeDot = dotCount > 0 ? activeIndex % dotCount : 0;
  const secondActiveDot = dotCount > 1 ? (activeDot + 1) % dotCount : -1;

  useEffect(() => {
    let cancelled = false;

    async function loadReviews() {
      try {
        setLoading(true);
        setError(null);

        const instanceRes = await fetch(
          `${FIRESTORE_BASE}/instances/${widgetInstanceId}`
        );
        if (!instanceRes.ok) throw new Error(`instance ${instanceRes.status}`);
        const instanceJson = await instanceRes.json();
        const instance = unwrapFirestoreFields(instanceJson.fields);
        const placeId = instance.places?.[0]?.placeId;
        if (!placeId) throw new Error('no placeId');

        const reviewsRes = await fetch(
          `${FIRESTORE_BASE}/places/${placeId}/reviews?orderBy=${encodeURIComponent(
            'time desc'
          )}`
        );
        if (!reviewsRes.ok) throw new Error(`reviews ${reviewsRes.status}`);
        const reviewsJson = await reviewsRes.json();

        const docs = reviewsJson.documents || [];
        const parsed = docs
          .map((doc) => {
            const f = unwrapFirestoreFields(doc.fields);
            return {
              id: doc.name,
              author: (f.author_name || '').toUpperCase(),
              date: formatReviewDate(f.time),
              text: (f.text || '').trim(),
              rating: Math.max(1, Math.min(5, f.rating || 5)),
              avatar: f.profile_photo_url,
              authorUrl: f.author_url,
            };
          })
          .filter((r) => r.text.length > 0);

        if (cancelled) return;

        if (parsed.length === 0) {
          setReviews(FALLBACK_REVIEWS);
        } else {
          setReviews(parsed);
          setLiveCount(parsed.length);
          const avg =
            parsed.reduce((s, r) => s + r.rating, 0) / parsed.length;
          setLiveRating(Math.round(avg * 10) / 10);
        }
      } catch (e) {
        if (cancelled) return;
        setError(e);
        setReviews(FALLBACK_REVIEWS);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadReviews();
    return () => {
      cancelled = true;
    };
  }, [widgetInstanceId]);

  const displayRating = liveRating ?? ratingProp;
  const displayCount = liveCount ?? reviewCountProp;

  return (
    <section
      className="w-full py-12 sm:py-16 px-4 sm:px-6 lg:px-8 overflow-hidden"
      style={{ backgroundColor: '#F2F6EF' }}
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 lg:gap-8 items-start">

          {/* LEFT: Company Card */}
          <div className="bg-white rounded-xl shadow-sm p-5 sm:p-6 w-full lg:max-w-[260px]">
            <div className="flex items-start gap-3">
              <LogoRing />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#1f2a1a] text-[14px] leading-tight">
                  {companyName}
                </p>
                <div className="flex items-center gap-1.5 mt-1.5">
                  {loading ? (
                    <span className="inline-block h-4 w-8 rounded bg-gray-200 animate-pulse" />
                  ) : (
                    <span className="font-bold text-[#1f2a1a] text-sm">{displayRating}</span>
                  )}
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5" fill="#FFC107" color="#FFC107" />
                    ))}
                  </div>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  Based on{' '}
                  {loading ? (
                    <span className="inline-block h-3 w-10 rounded bg-gray-200 animate-pulse align-middle" />
                  ) : (
                    <span className="font-semibold">reviews</span>
                  )}
                </p>
                <div className="flex items-center gap-1 mt-1.5 text-[11px] text-gray-700">
                  <span>powered by</span>
                  <GoogleLogo className="h-3.5" />
                </div>
              </div>
            </div>

            <a
              href={reviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center justify-center gap-2 px-4 py-2 rounded text-white text-sm font-medium w-full hover:opacity-90 transition-opacity"
              style={{ backgroundColor: brandGreen }}
            >
              Review us on <GoogleG className="w-4 h-4" />
            </a>
          </div>

          {/* RIGHT: Review Slider */}
          <div className="relative w-full min-w-0">
            <div className="overflow-hidden sm:px-12">
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                  <SkeletonCard borderGreen={borderGreen} cardBg={cardBg} />
                  <SkeletonCard borderGreen={borderGreen} cardBg={cardBg} />
                </div>
              ) : (
                <Swiper
                  modules={[Autoplay]}
                  onSwiper={(s) => {
                    swiperRef.current = s;
                    setActiveIndex(s.realIndex || 0);
                  }}
                  onSlideChange={(s) => setActiveIndex(s.realIndex || 0)}
                  spaceBetween={20}
                  slidesPerView={1}
                  breakpoints={{
                    768: { slidesPerView: 2, spaceBetween: 24 },
                  }}
                  loop={reviews.length > 2}
                  autoplay={{
                    delay: autoplayInterval,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: true,
                  }}
                  speed={500}
                  className="pb-4"
                >
                  {reviews.map((t) => (
                    <SwiperSlide key={t.id}>
                      <ReviewCard review={t} borderGreen={borderGreen} cardBg={cardBg} />
                    </SwiperSlide>
                  ))}
                </Swiper>
              )}
            </div>

            {!loading && (
              <>
                <button
                  onClick={() => swiperRef.current?.slidePrev()}
                  aria-label="Previous review"
                  className="hidden sm:flex absolute left-0 top-[38%] -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white border border-gray-200 items-center justify-center shadow-sm hover:bg-gray-50 transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-400" />
                </button>
                <button
                  onClick={() => swiperRef.current?.slideNext()}
                  aria-label="Next review"
                  className="hidden sm:flex absolute right-0 top-[38%] -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white border border-gray-200 items-center justify-center shadow-sm hover:bg-gray-50 transition-colors"
                >
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
              </>
            )}

            {!loading && dotCount > 0 && (
              <div className="flex justify-center items-center gap-2 mt-6">
                {Array.from({ length: dotCount }).map((_, i) => {
                  const isActive = i === activeDot || i === secondActiveDot;
                  return (
                    <button
                      key={i}
                      type="button"
                      aria-label={`Go to review group ${i + 1}`}
                      onClick={() => {
                        const swiper = swiperRef.current;
                        if (!swiper) return;
                        if (typeof swiper.slideToLoop === 'function' && reviews.length > 2) {
                          swiper.slideToLoop(i);
                        } else {
                          swiper.slideTo(i);
                        }
                      }}
                      className={`testimonial-dot ${isActive ? 'testimonial-dot-active' : ''}`}
                    />
                  );
                })}
              </div>
            )}

            {error && (
              <p className="text-center text-[11px] text-gray-400 mt-2">
                Showing offline reviews — couldn’t reach live source.
              </p>
            )}
          </div>

        </div>
      </div>

      <style>{`
          .testimonial-dot {
            width: 9px;
            height: 9px;
            background: #355931;
            border: 0;
            padding: 0;
            border-radius: 9999px;
            display: inline-block;
            cursor: pointer;
            opacity: 0.5;
            position: relative;
            transition: background-color 0.3s ease, opacity 0.3s ease, width 0.3s ease, height 0.3s ease;
          }

          .testimonial-dot::after {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 44px;
            height: 44px;
            border-radius: 9999px;
          }

          .testimonial-dot:hover { opacity: 1; }

          .testimonial-dot-active {
            background: #B8D32A;
            width: 12px;
            height: 12px;
            opacity: 1;
          }
        @keyframes shimmer {
          0% { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }
        .skeleton-shimmer {
          background: linear-gradient(90deg, #eef1ec 0%, #f7faf5 50%, #eef1ec 100%);
          background-size: 800px 100%;
          animation: shimmer 1.4s linear infinite;
        }
      `}</style>
    </section>
  );
}

/* ---------------- Review Card ---------------- */
function ReviewCard({ review, borderGreen, cardBg }) {
  const [expanded, setExpanded] = useState(false);
  const PREVIEW = 180;
  const isLong = review.text.length > PREVIEW;
  const shown = !isLong || expanded ? review.text : review.text.slice(0, PREVIEW).trimEnd();

  return (
    <div className="relative pb-20 px-1">
      <div
        className="relative rounded-2xl p-5 sm:p-6 min-h-[200px]"
        style={{ backgroundColor: cardBg, border: `1.5px solid ${borderGreen}` }}
      >
        <div
          className="absolute top-2 left-4 text-[54px] leading-none select-none font-serif"
          style={{ color: '#C9C9C9' }}
          aria-hidden="true"
        >
          &ldquo;
        </div>

        <div className="flex items-start gap-2 pl-10 pt-1">
          <div className="flex gap-0.5 pt-0.5 flex-shrink-0">
            {[...Array(review.rating)].map((_, i) => (
              <Star key={i} className="w-4 h-4" fill="#FFC107" color="#FFC107" />
            ))}
          </div>
          <VerifiedBadge />
        </div>

        <p className="text-[13px] sm:text-sm text-gray-700 italic leading-relaxed pl-10 pr-2 mt-2">
          {shown}
          {isLong && !expanded && '... '}
          {isLong && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-gray-600 italic hover:text-gray-800 underline-offset-2 hover:underline"
            >
              {expanded ? ' show less' : 'read more'}
            </button>
          )}
        </p>

        <div className="absolute bottom-3 right-4">
          <GoogleG className="w-6 h-6" />
        </div>

        <div className="absolute left-10 -bottom-[9px]">
          <div
            className="w-4 h-4 rotate-45"
            style={{
              backgroundColor: cardBg,
              borderRight: `1.5px solid ${borderGreen}`,
              borderBottom: `1.5px solid ${borderGreen}`,
            }}
          />
        </div>
      </div>

      <div className="flex items-center gap-3 mt-5 pl-4">
        <img
          src={review.avatar || 'https://i.pravatar.cc/80'}
          alt={review.author}
          className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border border-gray-200 flex-shrink-0"
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={(e) => {
            e.currentTarget.src = 'https://i.pravatar.cc/80';
          }}
        />
        <div className="min-w-0">
        <p className="text-xs sm:text-sm font-semibold tracking-wide uppercase"
            style={{ color: '#5a3a1a' }}
          >
            {review.author}
          </p>
          <p className="text-[11px] sm:text-xs text-gray-700 uppercase tracking-wide">
            {review.date}
          </p>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Skeleton Card ---------------- */
function SkeletonCard({ borderGreen, cardBg }) {
  return (
    <div className="relative pb-20 px-1">
      <div
        className="relative rounded-2xl p-5 sm:p-6 min-h-[200px]"
        style={{ backgroundColor: cardBg, border: `1.5px solid ${borderGreen}` }}
      >
        <div className="flex items-center gap-2 pl-10 pt-1">
          <div className="h-4 w-24 rounded skeleton-shimmer" />
          <div className="h-4 w-4 rounded-full skeleton-shimmer" />
        </div>
        <div className="space-y-2 pl-10 pr-2 mt-3">
          <div className="h-3 w-[95%] rounded skeleton-shimmer" />
          <div className="h-3 w-[90%] rounded skeleton-shimmer" />
          <div className="h-3 w-[80%] rounded skeleton-shimmer" />
          <div className="h-3 w-[60%] rounded skeleton-shimmer" />
        </div>
      </div>
      <div className="flex items-center gap-3 mt-5 pl-4">
        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full skeleton-shimmer flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-3 w-32 rounded skeleton-shimmer" />
          <div className="h-2.5 w-20 rounded skeleton-shimmer" />
        </div>
      </div>
    </div>
  );
}

/* ---------------- Small SVG bits ---------------- */
function LogoRing() {
  return (
    <div className="flex-shrink-0">
      <img
        src={nutraLogof}
        alt="logo"
        className="w-12 h-12 object-contain"
      />
    </div>
  );
}

function VerifiedBadge() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0 mt-0.5">
      <path
        fill="#9AA0A6"
        d="M22.5 12l-2.25-2.57.31-3.41-3.34-.76-1.75-2.93L12 3.66 8.53 2.33 6.78 5.26l-3.34.75.31 3.41L1.5 12l2.25 2.57-.31 3.42 3.34.76 1.75 2.93L12 20.34l3.47 1.33 1.75-2.93 3.34-.76-.31-3.41L22.5 12z"
      />
      <path fill="#FFFFFF" d="M10.09 16.27L6.3 12.48l1.41-1.41 2.38 2.38 6.18-6.18 1.41 1.42z" />
    </svg>
  );
}

function GoogleG({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

function GoogleLogo({ className = 'h-4' }) {
  return (
    <svg className={className} viewBox="0 0 272 92" xmlns="http://www.w3.org/2000/svg">
      <path fill="#EA4335" d="M115.75 47.18c0 12.77-9.99 22.18-22.25 22.18S71.25 59.95 71.25 47.18C71.25 34.32 81.24 25 93.5 25s22.25 9.32 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44S80.99 39.2 80.99 47.18c0 7.9 5.79 13.44 12.51 13.44s12.51-5.55 12.51-13.44z" />
      <path fill="#FBBC05" d="M163.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18c0-12.85 9.99-22.18 22.25-22.18s22.25 9.32 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44s-12.51 5.46-12.51 13.44c0 7.9 5.79 13.44 12.51 13.44s12.51-5.55 12.51-13.44z" />
      <path fill="#4285F4" d="M209.75 26.34v39.82c0 16.38-9.66 23.07-21.08 23.07-10.75 0-17.22-7.19-19.66-13.07l8.48-3.53c1.51 3.61 5.21 7.87 11.17 7.87 7.31 0 11.84-4.51 11.84-13v-3.19h-.34c-2.18 2.69-6.38 5.04-11.68 5.04-11.09 0-21.25-9.66-21.25-22.09 0-12.52 10.16-22.26 21.25-22.26 5.29 0 9.49 2.35 11.68 4.96h.34v-3.61h9.25zm-8.56 20.92c0-7.81-5.21-13.52-11.84-13.52-6.72 0-12.35 5.71-12.35 13.52 0 7.73 5.63 13.36 12.35 13.36 6.63 0 11.84-5.63 11.84-13.36z" />
      <path fill="#34A853" d="M225 3v65h-9.5V3h9.5z" />
      <path fill="#EA4335" d="M262.02 54.48l7.56 5.04c-2.44 3.61-8.32 9.83-18.48 9.83-12.6 0-22.01-9.74-22.01-22.18 0-13.19 9.49-22.18 20.92-22.18 11.51 0 17.14 9.16 18.98 14.11l1.01 2.52-29.65 12.28c2.27 4.45 5.8 6.72 10.75 6.72 4.96 0 8.4-2.44 10.92-6.14zm-23.27-7.98l19.82-8.23c-1.09-2.77-4.37-4.7-8.23-4.7-4.95 0-11.84 4.37-11.59 12.93z" />
      <path fill="#4285F4" d="M35.29 41.41V32H67c.31 1.64.47 3.58.47 5.68 0 7.06-1.93 15.79-8.15 22.01-6.05 6.3-13.78 9.66-24.02 9.66C16.32 69.35.36 53.89.36 34.91S16.32.47 35.3.47c10.5 0 17.98 4.12 23.6 9.49l-6.64 6.64c-4.03-3.78-9.49-6.72-16.97-6.72-13.86 0-24.7 11.17-24.7 25.03 0 13.86 10.84 25.03 24.7 25.03 8.99 0 14.11-3.61 17.39-6.89 2.66-2.66 4.41-6.46 5.1-11.65l-22.49.01z" />
    </svg>
  );
}
